import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { titleCase } from '../../lib/format'
import { useAuth } from '../../lib/auth'
import { portalFor } from '../../lib/roles'
import { useStorageUpload, uploadedFileFields } from '../../lib/upload'
import type { UploadedFile } from '../../lib/upload'
import type { CourseProgressRecord, CourseSummary, UserProfile } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton, StatusBadge } from '../../components/ui'
import { CloseIcon, GradCapIcon, PlusIcon } from '../../components/icons'

interface CourseProgressResponse {
  ok: boolean
  items: CourseProgressRecord[]
  total_enrolments: number
  completions: number
  avg_score: number
}

/* The "record physical training" form below is admin-portal-only - it's a
 * faithful port of admin/routes.py's record_physical_training(), which is
 * itself only reachable at /admin/course-progress/manual (the gov portal
 * has its own, form-less course-progress view with no equivalent Jinja
 * route to port). */
export default function CourseProgress() {
  const { user } = useAuth()
  const isAdmin = portalFor(user) === 'admin'
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedUid, setSelectedUid] = useState('')
  const [proofFile, setProofFile] = useState<UploadedFile | null>(null)
  const upload = useStorageUpload(`training_proof/${user?.org_id || 'platform'}`)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gov', 'course-progress'],
    queryFn: () => api.get<CourseProgressResponse>('/gov/course-progress'),
  })
  const usersQuery = useQuery({
    queryKey: ['admin', 'users', 'all-for-training'],
    queryFn: () => api.get<{ ok: boolean; items: UserProfile[] }>('/admin/users?status=&limit=1000'),
    enabled: isAdmin && showForm,
  })
  const coursesQuery = useQuery({
    queryKey: ['learning', 'courses'],
    queryFn: () => api.get<{ ok: boolean; courses: CourseSummary[] }>('/learning/courses'),
    enabled: isAdmin && showForm,
  })

  const record = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/admin/course-progress/manual', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gov', 'course-progress'] })
      setShowForm(false)
      setSelectedUid('')
      setSearch('')
      setProofFile(null)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    record.mutate({
      uid: selectedUid,
      course_id: String(form.get('course_id') ?? ''),
      completed_at: String(form.get('completed_at') ?? ''),
      trainer: String(form.get('trainer') ?? '').trim(),
      objectives: String(form.get('objectives') ?? '').trim(),
      score: String(form.get('score') ?? '').trim(),
      notes: String(form.get('notes') ?? '').trim(),
      ...uploadedFileFields(proofFile, 'proof'),
    })
  }

  const items = data?.items ?? []
  const q = search.trim().toLowerCase()
  const userMatches = q
    ? (usersQuery.data?.items ?? [])
        .filter((u) => (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))
        .slice(0, 20)
    : []
  const selectedUser = usersQuery.data?.items.find((u) => u.uid === selectedUid)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Training progress</h1>
          <p className="page-head__sub">
            {isAdmin ? 'Course completion across the platform.' : 'Course completion across your jurisdiction.'}
          </p>
        </div>
        {isAdmin && (
          <>
            <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
              {showForm ? 'Cancel' : 'Record physical training'}
            </Button>
            <button
              type="button" className="fab"
              onClick={() => setShowForm((s) => !s)}
              aria-label={showForm ? 'Cancel' : 'Record physical training'}
            >
              {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
            </button>
          </>
        )}
      </div>

      {isAdmin && showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Record physical training" sub="For training completed in person, outside the app." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {record.isError && (
                  <Alert tone="danger">
                    {record.error instanceof ApiError ? record.error.message : 'Could not record that training.'}
                  </Alert>
                )}
                {selectedUser ? (
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Trainee: <strong>{selectedUser.full_name || selectedUser.email}</strong></span>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedUid('')}>Change</Button>
                  </div>
                ) : (
                  <div>
                    <Input label="Find trainee" value={search} onChange={(e) => setSearch(e.target.value)}
                           placeholder="Search by name or email…" />
                    {userMatches.length > 0 && (
                      <div className="stack" style={{ marginTop: 'var(--sp-2)', maxHeight: 220, overflowY: 'auto' }}>
                        {userMatches.map((u) => (
                          <button type="button" key={u.uid} className="btn btn--ghost btn--sm btn--block"
                                  style={{ justifyContent: 'flex-start' }}
                                  onClick={() => { setSelectedUid(u.uid); setSearch('') }}>
                            {u.full_name || u.email} <span style={{ opacity: 0.6, marginLeft: 6 }}>{u.email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Select label="Course" name="course_id" required defaultValue="">
                  <option value="" disabled>Select a course…</option>
                  {(coursesQuery.data?.courses ?? []).map((c) => (
                    <option key={c.course_id} value={c.course_id}>{c.course_name}</option>
                  ))}
                </Select>
                <div className="grid grid--2">
                  <Input label="Completed on" name="completed_at" type="date" />
                  <Input label="Score (optional)" name="score" type="number" min="0" max="100" />
                </div>
                <Input label="Trainer" name="trainer" />
                <Input label="Objectives (optional)" name="objectives" />
                <Input label="Notes (optional)" name="notes" />
                <div>
                  <label className="field__label">Proof of attendance (optional)</label>
                  <input type="file" accept="image/*,application/pdf"
                         onChange={(e) => {
                           const f = e.target.files?.[0]
                           if (f) upload.upload(f).then(setProofFile).catch(() => {})
                         }} />
                  {upload.progress != null && <p style={{ fontSize: 'var(--text-sm)' }}>Uploading… {upload.progress}%</p>}
                  {upload.error && <Alert tone="danger">{upload.error}</Alert>}
                  {proofFile && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>Attached: {proofFile.name}</p>}
                </div>
                <div className="row">
                  <Button type="submit" loading={record.isPending} disabled={!selectedUid} confirm={{ title: "Record this training?" }}>Record training</Button>
                </div>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {isError ? (
        <Alert tone="danger">{(error as Error).message}</Alert>
      ) : (
        <>
          <div className="grid grid--tiles-2" style={{ marginBottom: 'var(--sp-6)' }}>
            <Card>
              <CardBody>
                <div className="stat__label">Enrolments</div>
                <div className="stat__value" style={{ fontSize: 'var(--text-lg)' }}>
                  {isLoading ? <Skeleton h={28} w="50%" /> : data?.total_enrolments ?? 0}
                </div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <div className="stat__label">Completions</div>
                <div className="stat__value" style={{ fontSize: 'var(--text-lg)' }}>
                  {isLoading ? <Skeleton h={28} w="50%" /> : `${data?.completions ?? 0} (avg ${data?.avg_score ?? 0}%)`}
                </div>
              </CardBody>
            </Card>
          </div>

          <Card>
            <CardHeader title="By person" />
            <CardBody flush>
              {isLoading ? (
                <div style={{ padding: 'var(--sp-6)' }}><Skeleton h={20} /></div>
              ) : items.length === 0 ? (
                <EmptyState icon={<GradCapIcon size={22} />} title="No training activity yet" body="" />
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Person</th><th>Role</th><th>Course</th><th className="table__num">Progress</th></tr></thead>
                    <tbody>
                      {items.map((p, i) => (
                        <tr key={`${p.user_id}-${p.course_id}-${i}`}>
                          <td>{p.user_name || p.user_email || p.user_id}</td>
                          <td>{titleCase(p.user_role)}</td>
                          <td>{p.course_name}</td>
                          <td className="table__num">
                            {p.is_completed ? <StatusBadge status="completed" /> : `${p.progress_pct ?? 0}%`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </>
  )
}
